import React, { useEffect, useState, useMemo, useCallback } from 'react';
import ReusableButton from '../common/Button/Button';
import operationAPI from '../../api/operationApi';
import { useForm, SubmitHandler } from 'react-hook-form';
import { AddDriver } from '../../type/addDriver';
import { IStaffAllocation } from '../../type/staffAllocation';
import { IRouteDetails } from '../../type/routeDetails';
import { IBusDetails } from '../../type/busDetails';
import { IBalloonDetails } from '../../type/balloonDetails';
import { IStaffDetails } from '../../type/driver';
import CrossIcon from '../../assets/CrossIcon';
import useLocationStore from '../../store/locatonStore';
import { StaffConstant } from '../../constants/constant';
import CheckIcon from '../../assets/CheckIcon';
import { encodeDateForUrl, formatStartTime } from '../../utils/dateUtils';
import { toast } from 'react-toastify';
import { translate } from '../../i18n';
import RedCrossIcon from '../../assets/icon/RedCrossIcon';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';
interface DriverFormProps {
  heading: string;
  closeModal: () => void;
  updateDriverListOperations?: IStaffAllocation[];
  routes: IRouteDetails[];
  buses: IBusDetails[];
  balloons: IBalloonDetails[];
  operationStaffId?: number;
  initialValues?: IStaffAllocation | null;
  onStaffChange: () => void;
  staffAllocation?: IStaffAllocation[] | null;
  date: Date;
}

const DriverForm: React.FC<DriverFormProps> = ({
  heading,
  closeModal,
  updateDriverListOperations,
  routes,
  buses,
  balloons,
  operationStaffId,
  initialValues,
  onStaffChange,
  staffAllocation,
  date,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<AddDriver>();
  const { selectedLocation } = useLocationStore();
  const [isLoading, setIsLoading] = useState(false);
  const [driverList, setDriverList] = useState<IStaffDetails[]>();
  const [maxPos, setMaxPos] = useState<number>();
  const [addAnother, setAddAnother] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<number>();

  const fetchStaffList = useCallback(async () => {
    try {
      const response = await operationAPI.driverList(
        selectedLocation.OwnerId,
        encodeDateForUrl(date),
      );
      const driverList: IStaffDetails[] = [];
      response?.data?.map((driver: IStaffDetails) => {
        driverList.push(driver);
      });
      if (initialValues?.staffId) {
        const alreadyExists = driverList.some(
          (driver) => driver.staffId === initialValues.staffId,
        );

        if (!alreadyExists) {
          const staffFromInitialValues = response?.data?.find(
            (driver: IStaffDetails) => driver.staffId === initialValues.staffId,
          );
          if (staffFromInitialValues) {
            driverList.push(staffFromInitialValues);
          }
        }
      }
      setDriverList(driverList);
    } catch (error) {
      console.log(error);
    }
  }, [selectedLocation.OwnerId, date, staffAllocation, initialValues]);

  useEffect(() => {
    fetchStaffList();
    const pos = getMaxPos(staffAllocation ?? []);
    setMaxPos(pos + 1);
  }, []);
  
  useEffect(() => {
    if (initialValues?.staffId||initialValues?.balloonId||initialValues?.busId) {
      const formattedTime = initialValues?.startTime
        ? new Date(initialValues.startTime).toISOString().substring(11, 16)
        : '';
      const bat = initialValues?.busArrivalTime
        ? new Date(initialValues.busArrivalTime).toISOString().substring(11, 16)
        : '';
      const endTime = initialValues?.endTime
        ? new Date(initialValues.endTime).toISOString().substring(11, 16)
        : '';
      setValue('staffId', initialValues?.staffId);
      setValue('routeId', initialValues?.routeId);
      setValue('busId', initialValues?.busId);
      setValue('balloonId', initialValues?.balloonId);
      setValue('startTime', formattedTime);
      setValue('endTime', endTime);
      setValue('bat', bat);
      if (!selectedStaffId) {
        setSelectedStaffId(initialValues?.staffId);
      }
    }
  }, [initialValues]);

  function sanitizeData(data: any): any {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) =>
        value === '' ? [key, null] : [key, value],
      ),
    );
  }

  const getMaxPos = (staffAllocation: IStaffAllocation[]): number => {
    if (staffAllocation.length === 0) return 0;
    return Math.max(...staffAllocation.map((staff) => staff.pos));
  };

  const onSubmit: SubmitHandler<AddDriver> = async (data) => {
    setIsLoading(true);
    if (operationStaffId||initialValues?.balloonId||initialValues?.busId) {
      try {
        const formattedStartTime = data?.startTime
          ? formatStartTime(data?.startTime)
          : undefined;
        const formattedEndTime = data.endTime
          ? formatStartTime(data?.endTime)
          : undefined;
        const batTime = data?.bat ? formatStartTime(data.bat) : undefined;
        const sanitizedData = sanitizeData({
          ...data,
          ownerId: selectedLocation.OwnerId,
          roleId: StaffConstant.DRIVER,
          startTime: formattedStartTime,
          endTime: formattedEndTime,
          bat: batTime,
          operationStaffId: operationStaffId,
          date: encodeDateForUrl(date),
        });
        await operationAPI.updateDriver(sanitizedData);
        setIsLoading(false);
        toast.success(translate('operation.driverUpdatedSuccessfully'));
        if (addAnother) {
          reset();
          setIsLoading(false);
        } else {
          setTimeout(() => {
            closeModal();
            onStaffChange();
          }, 500);
        }
      } catch (error) {
        setIsLoading(false);
        toast.warn(translate('operation.commonError'));
        closeModal();
      }
    } else {
      try {
        const formattedStartTime = data?.startTime
          ? formatStartTime(data?.startTime)
          : undefined;
        const formattedEndTime = data?.endTime
          ? formatStartTime(data?.endTime)
          : undefined;
        const batTime = data?.bat ? formatStartTime(data?.bat) : undefined;
        const sanitizedData = sanitizeData({
          ...data,
          ownerId: selectedLocation.OwnerId,
          startTime: formattedStartTime,
          endTime: formattedEndTime,
          bat: batTime,
          date: encodeDateForUrl(date),
          roleId: StaffConstant.DRIVER,
          pos: maxPos,
          // staffId:null
        });
         await operationAPI.addDriver(sanitizedData);
        toast.success(translate('operation.driverAddedSuccessfully'));
        if (addAnother) {
          reset();
          setIsLoading(false);
          setSelectedStaffId(0);
          onStaffChange();
        } else {
          setTimeout(() => {
            closeModal();
            onStaffChange();
          }, 500);
        }
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        console.error('Failed to add driver:', error);
        toast.warn(translate('operation.commonError'));
        closeModal();
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <div className="max-w-xl mx-auto  bg-sidebar-background p-6 rounded-lg shadow-md relative">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-textColorGray">
            {heading}
          </h2>
          <button
            className="text-textColorGray hover:text-textColorGray absolute top-0 right-0 p-2"
            onClick={closeModal}
          >
            <RedCrossIcon />
          </button>
        </div>
        <div className="p-4 border-t-2 rounded-b-md border-light-blue" />
        {/* Form */}
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="overflow-y-auto max-h-[calc(100vh-100px)] min-w-full grid grid-cols-2 gap-4">
            {/* Driver Name */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.driverName')}
              </label>
              <select
                {...register('staffId', {
                  onChange: (e) => {
                    const value = e.target.value;
                    setSelectedStaffId(value);
                    setValue('staffId', value);
                    const selectedDriver = driverList?.find(
                      (driver) => driver?.staffId === Number(value),
                    );
                    if (selectedDriver) {
                      setValue('phoneNumber', selectedDriver?.staffContact);
                    }
                  },
                  // required: translate('driver.driverNameValidation'),
                })}
                value={selectedStaffId} // Controlled value
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
              >
                <option value="">{translate('driver.driverSelection')}</option>
                {driverList?.map((driver) => (
                  <option key={driver?.staffId} value={driver?.staffId}>
                    {driver?.staffName}
                  </option>
                ))}
              </select>

              {/* Error message display */}
              {errors.staffId && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.staffId.message}
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.phoneNumber')}
              </label>
              <input
                {...register('phoneNumber')}
                type="text"
                placeholder={translate('driver.phoneNumberPlaceHolder')}
                readOnly
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                defaultValue={initialValues?.resPhone.replace(
                  /^[^\d]*(\d.*)/,
                  '$1',
                )}
              />
            </div>

            {/* Route */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.route')}
              </label>
              <select
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('routeId')}
              >
                <option value="">{translate('driver.selectRoute')}</option>
                {routes?.map((route) => (
                  <option key={route?.routeId} value={route?.routeId}>
                    {route?.routeCode}
                  </option>
                ))}
              </select>

              {/* Error message display */}
              {errors.routeId && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.routeId.message}
                </p>
              )}
            </div>

            {/* Bus */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.bus')}
              </label>
              <select
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('busId')}
              >
                <option value="">{translate('driver.selectBus')}</option>
                {buses?.map((bus) => (
                  <option key={bus?.busId} value={bus?.busId}>
                    {bus?.busName}
                  </option>
                ))}
              </select>

              {/* Error message display */}
              {errors.busId && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.busId.message}
                </p>
              )}
            </div>

            {/* Balloon */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.balloon')}
              </label>
              <select
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('balloonId')}
              >
                <option value="">{translate('driver.selectBalloon')}</option>
                {balloons?.map((balloon) => (
                  <option key={balloon?.balloonId} value={balloon?.balloonId}>
                    {balloon?.formattedBalloonName}
                  </option>
                ))}
              </select>

              {/* Error message display */}
              {errors.balloonId && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.balloonId.message}
                </p>
              )}
            </div>

            {/* Start Time */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.startTime')}
              </label>
              <input
                type="time"
                placeholder={translate('driver.startTimePlaceholder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('startTime')}
                defaultValue={initialValues?.startTime}
              />

              {/* Error message display */}
              {errors.startTime && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.startTime.message}
                </p>
              )}
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.endTime')}
              </label>
              <input
                type="time"
                placeholder={translate('driver.startTimePlaceholder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('endTime')}
                defaultValue={initialValues?.endTime}
              />

              {/* Error message display */}
              {errors.startTime && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.startTime.message}
                </p>
              )}
            </div>

            {/* Fuel */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.fuel')}
              </label>
              <input
                defaultValue={initialValues?.fuel ?? ''}
                type="text"
                placeholder={translate('driver.fuelPlaceholder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('fuel')}
              />

              {/* Error message display */}
              {errors.fuel && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.fuel.message}
                </p>
              )}
            </div>

            {/* Start Odo */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.startOdo')}
              </label>
              <input
                defaultValue={initialValues?.startOdo ?? ''}
                type="text"
                placeholder={translate('driver.startOdoPlaceholder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('startOdo')}
              />
              {/* Error message for Start Odo */}
              {errors.startOdo && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.startOdo.message}
                </p>
              )}
            </div>

            {/* End Odo */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.endOdo')}
              </label>
              <input
                defaultValue={initialValues?.endOdo ?? ''}
                type="text"
                placeholder={translate('driver.endOdoPlaceHolder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('endOdo')}
              />
              {/* Error message for End Odo */}
              {errors.endOdo && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.endOdo.message}
                </p>
              )}
            </div>

            {/* Bat */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.bat')}
              </label>

              <input
                type="time"
                placeholder={translate('driver.batPlaceholder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('bat', {
                  // required: translate('driver.batValidation'), // Validation message
                })}
                defaultValue={initialValues?.startTime}
              />

              {/* Error message for Bat */}
              {errors.bat && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.bat.message}
                </p>
              )}
            </div>

            {/* Checkboxes */}
            <div className="col-span-2 flex flex-col gap-2">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="w-5 h-5 mr-2 text-blue-600"
                  {...register('need')}
                  defaultChecked={initialValues?.need}
                />
                <span className="text-sm text-textColorGray">
                  {translate('operationCommon.need')}
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  defaultChecked={initialValues?.kkd}
                  type="checkbox"
                  className="w-5 h-5 mr-2 text-blue-600"
                  {...register('kkd')}
                />
                <span className="text-sm text-textColorGray">
                  {translate('operationCommon.kkd')}
                </span>
              </label>

              <label className="inline-flex items-center">
                <input
                  defaultChecked={initialValues?.preCheck}
                  type="checkbox"
                  className="w-5 h-5 mr-2 text-blue-600"
                  {...register('preCheck')}
                />
                <span className="text-sm text-textColorGray">
                  {translate('operationCommon.preCheck')}
                </span>
              </label>
            </div>
          </div>
          {/* Buttons */}
          <div className="col-span-2 flex mt-4">
            <div className="flex w-auto items-center space-x-2">
              <input
                type="checkbox"
                id="addAnother"
                className="w-5 h-5 border-light-blue"
                checked={addAnother}
                onChange={(e) => setAddAnother(e.target.checked)}
              />
              <label
                htmlFor="addAnother"
                className="text-sm whitespace-nowrap mb-1 text-textColorGray "
              >
                {translate('common.addAnother')}
              </label>
            </div>
            <div className="flex w-full justify-end">
              <ReusableButton
                variant="secondary"
                children={'Cancel'}
                icon={
                  <div>
                    <div className="block dark:hidden">
                      <CrossIcon />
                    </div>
                    <div className="hidden dark:block">
                      <CrossIconDarkForm />
                    </div>
                  </div>
                }
                onClick={closeModal}
                isDisabled={isLoading}
                className="w-auto bg-button-cancelButtonBgColor text-text-cancelButtonTextColor"
              />
              <ReusableButton
                icon={<CheckIcon />}
                children={'Confirm'}
                variant="primary"
                className="w-auto bg-background-backgroundColor"
                isDisabled={isLoading}
                isLoader={isLoading}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default DriverForm;
